import Header from "@/components/Header";
import Footer from "@/components/Footer";

const SECTIONS = [
  ["How listings are collected",
    "Rental criteria are contributed by the public and by our research team from publicly advertised rental listings. Contributors record only the criteria a listing advertises — such as rent, minimum credit score, income requirement, reserves, and cosigner policy. We do not solicit or record private or non-public information."],
  ["How experiences are submitted",
    "Renters, landlords, property managers, and housing professionals submit their experiences through a voluntary form. Participation is self-selected. Submissions are stored privately and are never published as individual stories."],
  ["How duplicate entries are handled",
    "Before a listing is counted, our research team checks for duplicates using listing source, location, and property details. Duplicate submissions of the same listing are merged or removed so that no property is counted more than once."],
  ["How data is verified",
    "No listing counts toward published statistics until a member of the research team reviews it and marks it Verified. Entries that cannot be verified, or that fall outside our scope, are marked Rejected or Archived and excluded from all figures."],
  ["What gets published",
    "We publish aggregate, anonymized statistics only — medians, percentages, counts, and county-level comparisons. Published reports rely on this aggregated data to describe patterns across the market."],
  ["What never gets published",
    "We never publish individual listings, individual experiences, or the names of any renter, landlord, agent, or brokerage. We do not produce rankings, ratings, or any 'worst of' lists. Personal identifying information is never published without explicit permission."],
  ["Limitations of the data",
    "Our data reflects listings and experiences that are voluntarily contributed and publicly advertised. It is not a random or representative sample of the entire California rental market, and advertised criteria may differ from criteria applied in practice. Figures are provisional and update as new verified data is added. We report these limitations openly so our findings are interpreted responsibly."],
];

export default function MethodologyPage() {
  return (
    <div className="min-h-screen bg-white font-body text-ink">
      <Header />

      <section className="mx-auto max-w-7xl px-6 pb-12 pt-20 md:px-12 md:pt-28">
        <div className="text-xs uppercase tracking-[0.2em] text-calblue">Methodology</div>
        <h1 className="mt-4 max-w-3xl font-serif text-5xl font-medium leading-[1.05] tracking-tight text-ink md:text-6xl">
          How we collect, verify, and publish data.
        </h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-700">
          Credible research depends on transparent methods. This page documents exactly how
          the California Rental Experience Project gathers information, protects privacy, and
          decides what to publish — so that journalists, researchers, and policymakers can
          judge our work on its merits.
        </p>
      </section>

      <section className="mx-auto max-w-4xl px-6 pb-32 md:px-12">
        <div className="space-y-px bg-slate-200">
          {SECTIONS.map(([title, body], i) => (
            <div key={title} data-testid={`method-${i + 1}`} className="bg-white py-8">
              <div className="flex gap-6">
                <span className="font-mono text-sm text-calblue">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h2 className="font-serif text-2xl font-medium leading-snug text-ink">{title}</h2>
                  <p className="mt-3 leading-relaxed text-slate-600">{body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 border-l-2 border-navy pl-6">
          <p className="max-w-2xl font-serif text-2xl font-normal leading-snug text-ink">
            We document. We verify. We publish only what the aggregate data supports — and
            nothing that identifies an individual without permission.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
